import * as Sentry from '@sentry/react'
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext.tsx'
import { getTestTopics } from '@/features/Testing/services/getTestTopics.ts'
import {
  getKvsCredentials,
  initKvsMasterConnection,
  KvsMasterHandle,
  KvsStreamConfig
} from '@/features/Testing/services/kvsStream'
import { selectTestTopics } from '@/features/Testing/services/selectTestTopics.ts'
import { sendTestReply } from '@/features/Testing/services/sendTestReply.ts'
import { simplifyQuestion } from '@/features/Testing/services/simplifyQuestion.ts'
import { startTest } from '@/features/Testing/services/startTest.ts'
import {
  RealtimeMessage,
  useRealtimeChannel
} from '@/hooks/useRealtimeChannel.ts'
import { ROUTES } from '@/types/routes.ts'

export enum MessageType {
  TOPIC_SELECTION = 'topic-selection',
  ICEBREAK_DIVISOR = 'icebreak-divisor',
  ICEBREAK_START = 'icebreak-start',
  USER = 'user',
  ASSISTANT = 'assistant',
  END = 'end'
}

enum MessageEvent {
  NEW_MESSAGE = 'new-message',
  PROCESSING_REPLY_FINISHED = 'processing-reply-finished'
}

type Message = {
  key: string
  type: MessageType
  event: MessageEvent
  pending: boolean
  data?: {
    role: 'assistant' | 'user'
    interactionType?: 'ask' | 'reply'
    content: string
    audioUrl?: string
    recordUrl?: string
    timeline?: string
    audioBlob?: Blob
    audioSeconds?: number
  }
}

type TestChatContextProps = {
  isUserTurn: boolean
  isTestFinished: boolean
  isStreamConnecting: boolean
  messages: Message[]
  availableTopics: string[]

  handleStart: () => void
  handleSimplifyQuestion: () => void
  handleSendUserReply: (audio: Blob, duration: number) => void
  handleSelectTopics: (topics: string[]) => void

  prefetchCredentials: () => void
  startStream: (stream: MediaStream) => Promise<void>
  stopStream: () => Promise<void>
}

const chatMessageDelaysMap = {
  [MessageType.ASSISTANT]: 3000,
  [MessageType.USER]: 1000,
  [MessageType.TOPIC_SELECTION]: 500,
  [MessageType.ICEBREAK_START]: 500,
  [MessageType.ICEBREAK_DIVISOR]: 500,
  [MessageType.END]: 3000
}

type ChatState = {
  queue: Message[]
  messages: Message[]
  isUserTurn: boolean
  isTestFinished: boolean
  nextMessageToRead: string | undefined
  keyCounter: number
}

type ChatAction =
  | { type: 'ENQUEUE_MESSAGE'; payload: Omit<Message, 'key'> }
  | { type: 'PROCESS_NEXT' }
  | { type: 'SCHEDULE_NEXT'; key: string }
  | { type: 'CLEAR_NEXT' }
  | { type: 'SET_USER_TURN'; payload: boolean }
  | { type: 'SET_TEST_FINISHED'; payload: boolean }

const initialState: ChatState = {
  queue: [],
  messages: [],
  isUserTurn: false,
  isTestFinished: false,
  nextMessageToRead: undefined,
  keyCounter: 0
}

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ENQUEUE_MESSAGE': {
      const keyCounter = state.keyCounter + 1
      const newMessage: Message = {
        ...action.payload,
        key: String(keyCounter)
      }
      const queue = [...state.queue, newMessage]
      return {
        ...state,
        queue,
        keyCounter,
        nextMessageToRead: state.nextMessageToRead ?? newMessage.key
      }
    }

    case 'PROCESS_NEXT': {
      const currentMessage = state.queue[0]
      if (!currentMessage) return state

      const remainingQueue = state.queue.slice(1)
      let { messages, isUserTurn } = state

      if (currentMessage.event === MessageEvent.NEW_MESSAGE) {
        const pendingIndex = messages.findIndex(
          (m) => m.type === currentMessage.type && m.pending
        )

        if (pendingIndex !== -1) {
          messages = messages.map((m, i) =>
            i === pendingIndex
              ? { ...m, pending: false, data: currentMessage.data }
              : m
          )
        } else {
          messages = [...messages, currentMessage]
        }
      }

      if (currentMessage.event === MessageEvent.PROCESSING_REPLY_FINISHED) {
        const lastMessage = messages[messages.length - 1]
        isUserTurn = lastMessage && lastMessage.type === MessageType.ASSISTANT
      }

      return {
        ...state,
        queue: remainingQueue,
        messages,
        isUserTurn
      }
    }

    case 'SCHEDULE_NEXT':
      return { ...state, nextMessageToRead: action.key }

    case 'CLEAR_NEXT':
      return { ...state, nextMessageToRead: undefined }

    case 'SET_USER_TURN':
      return { ...state, isUserTurn: action.payload }

    case 'SET_TEST_FINISHED':
      return { ...state, isTestFinished: action.payload }

    default:
      return state
  }
}

const TestChatContext = React.createContext<TestChatContextProps>(
  {} as TestChatContextProps
)

const TestChatProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()
  const { realtimeChannel, realtimeToken, configurations } = useAuth()

  const [state, dispatch] = useReducer(chatReducer, initialState)
  const { messages, isUserTurn, isTestFinished, nextMessageToRead, queue } =
    state

  const [availableTopics, setAvailableTopics] = useState<string[]>([])
  const [isStreamConnecting, setIsStreamConnecting] = useState(
    configurations.shouldRecordVideo
  )

  const kvsCredentials = useRef<KvsStreamConfig>(undefined)
  const kvsCredentialsPromise = useRef<Promise<KvsStreamConfig> | null>(null)
  const kvsMasterHandleRef = useRef<KvsMasterHandle | null>(null)

  const enqueue = useCallback(
    (message: Omit<Message, 'key'>) =>
      dispatch({ type: 'ENQUEUE_MESSAGE', payload: message }),
    []
  )

  const prefetchCredentials = useCallback(() => {
    if (!configurations.shouldRecordVideo) return
    if (kvsCredentials.current || kvsCredentialsPromise.current) return

    kvsCredentialsPromise.current = getKvsCredentials().then((config) => {
      kvsCredentials.current = config
      return config
    })
  }, [configurations.shouldRecordVideo])

  const startKvsStream = useCallback(async (stream: MediaStream) => {
    try {
      if (!kvsCredentials.current && kvsCredentialsPromise.current) {
        await kvsCredentialsPromise.current
      }
      if (!kvsCredentials.current) {
        kvsCredentials.current = await getKvsCredentials()
      }
      kvsMasterHandleRef.current = await initKvsMasterConnection(
        kvsCredentials.current,
        stream
      )
    } catch (error) {
      Sentry.captureException(error)
      console.error('KVS stream initialization failed:', error)
    } finally {
      setIsStreamConnecting(false)
    }
  }, [])

  const stopKvsStream = useCallback(async () => {
    if (kvsMasterHandleRef.current) {
      kvsMasterHandleRef.current.close()
      kvsMasterHandleRef.current = null
    }
  }, [])

  const handleStart = useCallback(async () => {
    while (configurations.shouldRecordVideo && !kvsMasterHandleRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    enqueue({
      type: MessageType.ICEBREAK_START,
      event: MessageEvent.NEW_MESSAGE,
      pending: false
    })

    enqueue({
      type: MessageType.ASSISTANT,
      event: MessageEvent.NEW_MESSAGE,
      pending: true
    })

    await startTest()
  }, [enqueue, configurations.shouldRecordVideo])

  const handleSendUserReply = useCallback(
    async (audio: Blob, duration: number) => {
      enqueue({
        type: MessageType.USER,
        event: MessageEvent.NEW_MESSAGE,
        pending: true,
        data: {
          role: 'user',
          content: '',
          audioBlob: audio,
          audioSeconds: duration
        }
      })
      dispatch({ type: 'SET_USER_TURN', payload: false })

      await sendTestReply(audio)
    },
    [enqueue]
  )

  const handleSelectTopics = useCallback(
    async (topics: string[]) => {
      enqueue({
        type: MessageType.ICEBREAK_DIVISOR,
        event: MessageEvent.NEW_MESSAGE,
        pending: true
      })

      enqueue({
        type: MessageType.ASSISTANT,
        event: MessageEvent.NEW_MESSAGE,
        pending: true
      })

      navigate(ROUTES.TESTING.TEST, { replace: true })

      await selectTestTopics(topics)
    },
    [enqueue, navigate]
  )

  const handleSimplifyQuestion = useCallback(async () => {
    dispatch({ type: 'SET_USER_TURN', payload: false })

    enqueue({
      type: MessageType.ASSISTANT,
      event: MessageEvent.NEW_MESSAGE,
      pending: true
    })

    await simplifyQuestion()
  }, [enqueue])

  const handleRealTimeMessage = useCallback(
    (realTimeMessage: RealtimeMessage) => {
      // console.log('message arrived', realTimeMessage)
      const data =
        (realTimeMessage?.data as {
          role: 'assistant' | 'user'
          content: string
          timeline: string
          interactionType: 'ask' | 'reply'
        }) ?? {}

      if (realTimeMessage && realTimeMessage.name === 'new-message') {
        enqueue({
          type: { user: MessageType.USER, assistant: MessageType.ASSISTANT }[
            data.role
          ],
          event: realTimeMessage.name,
          pending: false,
          data
        } as Message)

        if (data.timeline === 'end') {
          dispatch({ type: 'SET_TEST_FINISHED', payload: true })
          return
        }

        if (
          data.timeline === 'topic-selection' &&
          data.interactionType === 'ask'
        ) {
          enqueue({
            type: MessageType.TOPIC_SELECTION,
            event: realTimeMessage.name,
            pending: false
          } as Message)
        }
        if (
          ['icebreaker', 'chat'].includes(data.timeline) &&
          data.role === MessageType.USER
        ) {
          enqueue({
            type: MessageType.ASSISTANT,
            event: MessageEvent.NEW_MESSAGE,
            pending: true
          })
        }
      }
      if (
        realTimeMessage &&
        realTimeMessage.name === 'processing-reply-finished'
      ) {
        enqueue({
          type: MessageType.USER,
          event: realTimeMessage.name,
          pending: false
        } as Message)
      }
    },
    [enqueue]
  )

  useEffect(() => {
    if (!nextMessageToRead || queue.length === 0) return

    const currentMessage = queue[0]
    if (!currentMessage) return

    // console.log('currentMessage', currentMessage)
    dispatch({ type: 'PROCESS_NEXT' })

    const nextInQueue = queue[1]
    // console.log('next in queue', nextInQueue)
    if (nextInQueue) {
      const timer = setTimeout(() => {
        dispatch({ type: 'SCHEDULE_NEXT', key: nextInQueue.key })
      }, chatMessageDelaysMap[currentMessage.type] || 500)
      return () => clearTimeout(timer)
    }
  }, [nextMessageToRead, queue])

  useEffect(() => {
    const fetchTopics = async () => {
      setAvailableTopics(await getTestTopics())
    }

    fetchTopics()
  }, [])

  useRealtimeChannel(realtimeChannel, realtimeToken, handleRealTimeMessage)

  return (
    <TestChatContext.Provider
      value={{
        isUserTurn,
        isTestFinished,
        isStreamConnecting,
        messages,
        availableTopics,
        handleStart,
        handleSendUserReply,
        handleSelectTopics,
        handleSimplifyQuestion,
        prefetchCredentials,
        startStream: startKvsStream,
        stopStream: stopKvsStream
      }}
    >
      {children}
    </TestChatContext.Provider>
  )
}

export default TestChatProvider

export const useTestChat = () => React.useContext(TestChatContext)
